import {
    SlidingSync,
    MSC3575List,
    MSC3575RoomSubscription,
    MSC3575_WILDCARD,
    SlidingSyncEvent,
    SlidingSyncState,
} from 'matrix-js-sdk/lib/sliding-sync';
import { MatrixClient, EventType, Direction } from 'matrix-js-sdk';
import { logDev } from '../utils/loggerHelpers';
import { RTC_PROTOCOL_V1 } from '@phonehq/rtc-core';

const SLIDING_SYNC_TIMEOUT_MS = 20_000;

const INITIAL_ROOM_BATCH = 50;
const SPIDER_BATCH_SIZE = 50;
const SPIDER_DELAY_MS = 50;

const REQUIRED_STATE_FOR_LIST: string[][] = [
    [EventType.RoomEncryption, ''],
    [EventType.RoomAvatar, ''],
    [EventType.RoomCanonicalAlias, ''],
    [EventType.RoomCreate, ''],
    [EventType.RoomMember, MSC3575_WILDCARD],
    [EventType.RoomPowerLevels, ''],
    [RTC_PROTOCOL_V1.membershipStateType, MSC3575_WILDCARD],
];

const CALL_ROOM_SUBSCRIPTION = 'call_room';

function createDefaultLists(): Map<string, MSC3575List> {
    return new Map([
        ['all_rooms', {
            ranges: [[0, INITIAL_ROOM_BATCH - 1]],
            sort: ['by_recency'],
            timeline_limit: 10,
            required_state: REQUIRED_STATE_FOR_LIST,
        }],
        ['spaces', {
            ranges: [[0, 9]],
            sort: ['by_recency'],
            timeline_limit: 0,
            required_state: REQUIRED_STATE_FOR_LIST,
            filters: {
                room_types: ['m.space'],
            },
        }],
    ]);
}

export class SlidingSyncManager {
    private static _instance: SlidingSyncManager | null = null;
    private slidingSync: SlidingSync | null = null;
    private client: MatrixClient | null = null;
    private activeRoomId: string | null = null;
    private pinnedCallRoomId: string | null = null;
    private isRunning = false;
    private initialRestoreDone = false;
    private onMembersLoadedCallback: (() => void) | null = null;
    private onRoomReceivedCallback: ((roomId: string) => void) | null = null;
    private onInitialSyncComplete: (() => void) | null = null;
    private spideringAbortController: AbortController | null = null;
    private spideringComplete = false;
    private spideringCompleteWaiters: Array<(complete: boolean) => void> = [];

    static get instance(): SlidingSyncManager {
        if (!SlidingSyncManager._instance) {
            SlidingSyncManager._instance = new SlidingSyncManager();
        }
        return SlidingSyncManager._instance;
    }

    /**
     * Initialize sliding sync with the given client
     */
    setup(client: MatrixClient): SlidingSync {
        this.client = client;
        const baseUrl = client.baseUrl;

        const lists = createDefaultLists();

        const defaultSubscription: MSC3575RoomSubscription = {
            timeline_limit: 5,
            required_state: REQUIRED_STATE_FOR_LIST,
        };

        this.slidingSync = new SlidingSync(
            baseUrl,
            lists,
            defaultSubscription,
            client,
            SLIDING_SYNC_TIMEOUT_MS
        );

        this.slidingSync.addCustomSubscription('active_room', {
            timeline_limit: 50,
            required_state: [
                [MSC3575_WILDCARD, MSC3575_WILDCARD],
            ],
        });

        this.slidingSync.addCustomSubscription(CALL_ROOM_SUBSCRIPTION, {
            timeline_limit: 10,
            required_state: [
                [MSC3575_WILDCARD, MSC3575_WILDCARD],
            ],
        });

        if (this.pinnedCallRoomId) {
            this.subscribeCallRoom(this.pinnedCallRoomId);
        }

        this.setupEventListeners();

        logDev('Sliding sync initialized', { source: 'slidingSyncManager' });

        return this.slidingSync;
    }

    /**
     * Setup event listeners for sliding sync events
     */
    private setupEventListeners(): void {
        if (!this.slidingSync) return;

        let initialSyncDone = false;

        this.slidingSync.on(SlidingSyncEvent.Lifecycle, (state, resp, err) => {
            console.log('[SLIDING SYNC] Lifecycle event:', state);

            if (state === SlidingSyncState.Complete) {
                this.isRunning = true;
                console.log('[SLIDING SYNC] Sync complete, rooms:', resp?.rooms ? Object.keys(resp.rooms).length : 0);
                logDev('Sliding sync cycle complete', {
                    source: 'slidingSyncManager',
                    roomCount: resp?.rooms ? Object.keys(resp.rooms).length : 0,
                });
                if (!initialSyncDone) {
                    initialSyncDone = true;
                    console.log('[SLIDING SYNC] First sync complete!');

                    // Start spidering immediately — don't wait for member loading
                    this.startSpidering();

                    this.loadAllRoomMembers().then(() => {
                        console.log('[SLIDING SYNC] All room members loaded');
                        logDev('Initial room members load complete', {
                            source: 'slidingSyncManager',
                        });

                        if (!this.initialRestoreDone && this.onInitialSyncComplete) {
                            this.initialRestoreDone = true;
                            console.log('[SLIDING SYNC] Calling onInitialSyncComplete to restore room...');
                            this.onInitialSyncComplete();
                        }
                    }).catch(err => {
                        console.error('[SLIDING SYNC] Failed to load room members:', err);
                    });
                }
            }

            if (err) {
                console.error('Sliding sync error:', err);
            }
        });

        this.slidingSync.on(SlidingSyncEvent.RoomData, (roomId, roomData) => {
            logDev(`Room data received: ${roomId}`, {
                source: 'slidingSyncManager',
                initial: roomData.initial,
                timelineEvents: roomData.timeline?.length || 0,
            });
            if (roomId === this.activeRoomId && this.client) {
                const room = this.client.getRoom(roomId);
                if (room) {
                    room.loadMembersIfNeeded().catch((error) => {
                        console.error(`Failed to lazy load members for ${roomId}:`, error);
                    });
                }
            }

            if (roomData.initial && this.onRoomReceivedCallback) {
                this.onRoomReceivedCallback(roomId);
            }
        });
    }

    /**
     * Progressively load all rooms in the background after initial sync.
     * Expands the all_rooms range by SPIDER_BATCH_SIZE every SPIDER_DELAY_MS
     * until all rooms are loaded. Modeled after Element Web's spidering approach.
     */
    private startSpidering(): void {
        if (!this.slidingSync) return;

        this.spideringAbortController = new AbortController();
        const { signal } = this.spideringAbortController;

        const checkAndAdvance = () => {
            if (signal.aborted) return;

            const totalRooms = this.getRoomCount('all_rooms');
            const listData = this.slidingSync?.getListParams('all_rooms');
            if (!listData) return;

            const currentEnd = listData.ranges[0]?.[1] ?? INITIAL_ROOM_BATCH - 1;

            if (totalRooms === 0 || currentEnd >= totalRooms - 1) {
                // All rooms loaded — stop spidering
                logDev('Spidering complete, all rooms loaded', {
                    source: 'slidingSyncManager',
                    totalRooms,
                });
                this.slidingSync?.off(SlidingSyncEvent.Lifecycle, onLifecycle);
                this.markSpideringComplete();
                return;
            }

            setTimeout(() => {
                if (signal.aborted || !this.slidingSync) return;

                const newEnd = totalRooms > 0
                    ? Math.min(currentEnd + SPIDER_BATCH_SIZE, totalRooms - 1)
                    : currentEnd + SPIDER_BATCH_SIZE;

                if (newEnd > currentEnd) {
                    this.slidingSync.setListRanges('all_rooms', [[0, newEnd]]);
                    logDev('Spidering: expanded room range', {
                        source: 'slidingSyncManager',
                        newRange: [0, newEnd],
                        totalRooms,
                    });
                }
            }, SPIDER_DELAY_MS);
        };

        const onLifecycle = (state: SlidingSyncState) => {
            if (signal.aborted || state !== SlidingSyncState.Complete) return;
            checkAndAdvance();
        };

        this.slidingSync.on(SlidingSyncEvent.Lifecycle, onLifecycle);
        checkAndAdvance();

        signal.addEventListener('abort', () => {
            this.slidingSync?.off(SlidingSyncEvent.Lifecycle, onLifecycle);
        });
    }

    async setRoomVisible(roomId: string): Promise<void> {
        if (!this.slidingSync || !this.client) return;

        if (this.activeRoomId === roomId) {
            return;
        }

        this.activeRoomId = roomId;

        this.slidingSync.useCustomSubscription(roomId, 'active_room');

        const currentSubs = this.slidingSync.getRoomSubscriptions();
        currentSubs.add(roomId);
        this.slidingSync.modifyRoomSubscriptions(currentSubs);

        logDev(`Room subscription set: ${roomId}`, {
            source: 'slidingSyncManager',
            timeline_limit: 50,
        });

        const room = this.client.getRoom(roomId);
        if (room) {
            room.loadMembersIfNeeded().catch((error) => {
                console.error(`Failed to load members for room ${roomId}:`, error);
            });
        }
    }

    pinCallRoom(roomId: string): void {
        if (this.pinnedCallRoomId === roomId) return;
        if (this.pinnedCallRoomId) {
            this.unpinCallRoom(this.pinnedCallRoomId);
        }
        this.pinnedCallRoomId = roomId;
        this.subscribeCallRoom(roomId);
    }

    unpinCallRoom(roomId: string): void {
        if (this.pinnedCallRoomId !== roomId) return;
        this.pinnedCallRoomId = null;
        if (!this.slidingSync || this.activeRoomId === roomId) return;

        const currentSubs = this.slidingSync.getRoomSubscriptions();
        currentSubs.delete(roomId);
        this.slidingSync.modifyRoomSubscriptions(currentSubs);

        logDev(`Call room subscription removed: ${roomId}`, {
            source: 'slidingSyncManager',
        });
    }

    getPinnedCallRoomId(): string | null {
        return this.pinnedCallRoomId;
    }

    private subscribeCallRoom(roomId: string): void {
        if (!this.slidingSync) return;
        if (this.activeRoomId !== roomId) {
            this.slidingSync.useCustomSubscription(roomId, CALL_ROOM_SUBSCRIPTION);
        }
        const currentSubs = this.slidingSync.getRoomSubscriptions();
        currentSubs.add(roomId);
        this.slidingSync.modifyRoomSubscriptions(currentSubs);

        logDev(`Call room subscription pinned: ${roomId}`, {
            source: 'slidingSyncManager',
        });
    }

    setRoomHidden(roomId: string): void {
        if (!this.slidingSync) return;

        if (this.activeRoomId === roomId && this.pinnedCallRoomId === roomId) {
            this.activeRoomId = null;
            this.slidingSync.useCustomSubscription(roomId, CALL_ROOM_SUBSCRIPTION);
            this.slidingSync.modifyRoomSubscriptions(this.slidingSync.getRoomSubscriptions());
            return;
        }

        if (this.activeRoomId === roomId) {
            this.activeRoomId = null;

            const currentSubs = this.slidingSync.getRoomSubscriptions();
            currentSubs.delete(roomId);
            this.slidingSync.modifyRoomSubscriptions(currentSubs);

            logDev(`Room subscription removed: ${roomId}`, {
                source: 'slidingSyncManager',
            });
        }
    }

    async loadMoreMessages(roomId: string, limit: number = 50): Promise<boolean> {
        if (!this.client) return false;

        const room = this.client.getRoom(roomId);
        if (!room) return false;

        try {
            const timeline = room.getLiveTimeline();
            const canPaginate = timeline.getPaginationToken(Direction.Backward) !== null;

            if (!canPaginate) {
                logDev('No more messages to load', {
                    source: 'slidingSyncManager',
                    roomId,
                });
                return false;
            }

            await this.client.scrollback(room, limit);

            logDev(`Loaded ${limit} more messages`, {
                source: 'slidingSyncManager',
                roomId,
                totalEvents: timeline.getEvents().length,
            });

            return true;
        } catch (error) {
            console.error(`Failed to load more messages for ${roomId}:`, error);
            return false;
        }
    }

    async loadMoreRooms(listKey: string = 'all_rooms', additionalCount: number = 50): Promise<void> {
        if (!this.slidingSync) return;

        const listData = this.slidingSync.getListParams(listKey);
        if (!listData) return;

        const currentRange = listData.ranges[0] || [0, INITIAL_ROOM_BATCH - 1];
        const totalRooms = this.getRoomCount(listKey);
        const requestedEnd = currentRange[1] + additionalCount;
        const newEnd = totalRooms > 0 ? Math.min(requestedEnd, totalRooms - 1) : requestedEnd;

        if (newEnd <= currentRange[1]) return;

        this.slidingSync.setListRanges(listKey, [[0, newEnd]]);

        logDev(`Room list expanded: ${listKey}`, {
            source: 'slidingSyncManager',
            newRange: [0, newEnd],
        });
    }

    getRoomCount(listKey: string = 'all_rooms'): number {
        if (!this.slidingSync) return 0;
        return this.slidingSync.getListData(listKey)?.joinedCount || 0;
    }

    setOnMembersLoaded(callback: () => void): void {
        this.onMembersLoadedCallback = callback;
    }

    setOnRoomReceived(callback: (roomId: string) => void): void {
        this.onRoomReceivedCallback = callback;
    }

    setOnInitialSyncComplete(callback: () => void): void {
        this.onInitialSyncComplete = callback;
    }

    async loadAllRoomMembers(): Promise<void> {
        console.log('[LOAD MEMBERS] Starting to load members for all rooms...');

        if (!this.client) {
            console.warn('[LOAD MEMBERS] No client available!');
            return;
        }

        const rooms = this.client.getRooms();
        console.log(`[LOAD MEMBERS] Found ${rooms.length} rooms`);

        const loadPromises = rooms.map(async (room) => {
            try {
                await room.loadMembersIfNeeded();
            } catch (error) {
                logDev(`Failed to load members for room ${room.roomId}`, {
                    source: 'slidingSyncManager',
                    error: String(error),
                });
            }
        });

        await Promise.all(loadPromises);

        console.log('[LOAD MEMBERS] All room members loaded successfully');
        logDev('All room members loaded', {
            source: 'slidingSyncManager',
            roomCount: rooms.length,
        });

        if (this.onMembersLoadedCallback) {
            console.log('[LOAD MEMBERS] Calling onMembersLoadedCallback...');
            this.onMembersLoadedCallback();
        } else {
            console.warn('[LOAD MEMBERS] No onMembersLoadedCallback registered!');
        }
    }

    async loadRoomMembers(roomId: string): Promise<void> {
        if (!this.client) return;

        const room = this.client.getRoom(roomId);
        if (!room) return;

        try {
            await room.loadMembersIfNeeded();
            logDev(`Room members loaded: ${roomId}`, {
                source: 'slidingSyncManager',
                memberCount: room.getJoinedMemberCount(),
            });
        } catch (error) {
            console.error(`Failed to load members for room ${roomId}:`, error);
        }
    }

    isActive(): boolean {
        return this.isRunning;
    }

    isRoomListFullyLoaded(): boolean {
        return this.spideringComplete;
    }

    waitForRoomListFullyLoaded(timeoutMs: number): Promise<boolean> {
        if (this.spideringComplete) {
            return Promise.resolve(true);
        }
        return new Promise((resolve) => {
            const timer = setTimeout(() => {
                const index = this.spideringCompleteWaiters.indexOf(onComplete);
                if (index !== -1) {
                    this.spideringCompleteWaiters.splice(index, 1);
                }
                resolve(false);
            }, timeoutMs);
            const onComplete = (complete: boolean) => {
                clearTimeout(timer);
                resolve(complete);
            };
            this.spideringCompleteWaiters.push(onComplete);
        });
    }

    private markSpideringComplete(): void {
        this.spideringComplete = true;
        this.spideringCompleteWaiters.splice(0).forEach(waiter => waiter(true));
    }

    getSlidingSync(): SlidingSync | null {
        return this.slidingSync;
    }

    stop(): void {
        if (this.spideringAbortController) {
            this.spideringAbortController.abort();
            this.spideringAbortController = null;
        }

        this.slidingSync = null;
        this.client = null;
        this.activeRoomId = null;
        this.pinnedCallRoomId = null;
        this.isRunning = false;
        this.initialRestoreDone = false;
        this.onMembersLoadedCallback = null;
        this.onRoomReceivedCallback = null;
        this.onInitialSyncComplete = null;
        this.spideringComplete = false;
        this.spideringCompleteWaiters.splice(0).forEach(waiter => waiter(false));

        logDev('Sliding sync stopped', { source: 'slidingSyncManager' });
    }
}

export const slidingSyncManager = SlidingSyncManager.instance;
